/**
 * TC credit cards (UX-FLOWS F4 tiles, BE-4 data)
 *
 * Route: /accounting/credit-cards (page) — also embedded as the Wallets → Credit Cards tab.
 * Cards shown as cards, not a table; balances come from the ledger. Add / edit cards; Make Payment and
 * View Transactions open the card.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Grid, Alert, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Switch, FormControlLabel, CircularProgress, IconButton, Tooltip, InputAdornment, Chip, Divider,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, CreditCard as CardIcon, Payment as PaymentIcon, ReceiptLong as HistoryIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { formatCurrency, formatDate } from '../../utils/accounting/formatters';
import { can } from '../../utils/roleUtils';
import { creditCardService } from '../../services/accounting/creditCard.service';
import { serverMessage } from '../../utils/apiConvert';

const CARD_TYPES = [['VISA', 'Visa'], ['MASTERCARD', 'Mastercard'], ['AMEX', 'Amex']];
const EMPTY = { cardType: 'VISA', lastFour: '', cardName: '', cardholderName: '', bankIssuer: '', paymentDueDay: '', openingBalance: '', active: true };

const sessionUser = () => {
  try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
};

export const cardLabel = (c) => `${(CARD_TYPES.find(([v]) => v === c.cardType) || [])[1] || c.cardType} *${c.lastFour}`;

export const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

// Days until the next payment due day (null when the card has none).
export const daysUntilDue = (card) => {
  const day = Number(card?.paymentDueDay);
  if (!day) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let due = new Date(now.getFullYear(), now.getMonth(), day);
  if (due < today) due = new Date(now.getFullYear(), now.getMonth() + 1, day);
  return Math.round((due - today) / 86400000);
};

const TileRow = ({ label, children }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.35 }}>
    <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>{label}</Typography>
    <Typography component="div" sx={{ fontSize: fs.sm, fontWeight: fw.medium, textAlign: 'right' }}>{children}</Typography>
  </Box>
);

const CreditCards = ({ embedded = false }) => {
  const navigate = useNavigate();
  const canWrite = can(sessionUser(), 'finance', 'write');
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCards(await creditCardService.getCards());
    } catch (err) {
      setError(serverMessage(err, 'Failed to load credit cards'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (c) => {
    setForm({
      cardType: c.cardType, lastFour: c.lastFour, cardName: c.cardName || '', cardholderName: c.cardholderName || '',
      bankIssuer: c.bankIssuer || '', paymentDueDay: c.paymentDueDay ?? '', openingBalance: '', active: c.active !== false,
    });
    setEditing(c);
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const valid = /^\d{4}$/.test(form.lastFour) && form.cardName.trim() && form.cardholderName.trim() && form.bankIssuer.trim()
    && (form.paymentDueDay === '' || (Number(form.paymentDueDay) >= 1 && Number(form.paymentDueDay) <= 28))
    && (form.openingBalance === '' || /^\d+(\.\d{1,2})?$/.test(String(form.openingBalance)));

  const save = async () => {
    setBusy(true);
    setError(null);
    const data = {
      cardType: form.cardType,
      lastFour: form.lastFour,
      cardName: form.cardName.trim(),
      cardholderName: form.cardholderName.trim(),
      bankIssuer: form.bankIssuer.trim(),
      paymentDueDay: form.paymentDueDay === '' ? undefined : Number(form.paymentDueDay),
    };
    try {
      if (editing?.id) {
        await creditCardService.updateCard(editing.id, { ...data, active: form.active });
        setSnackbar('Card updated');
      } else {
        await creditCardService.createCard({ ...data, openingBalance: form.openingBalance === '' ? undefined : form.openingBalance, active: true });
        setSnackbar('Card added');
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(serverMessage(err, 'Failed to save the card'));
    } finally {
      setBusy(false);
    }
  };

  const open = (c, pay) => navigate(`/accounting/credit-cards/${c.id}${pay ? '?pay=1' : ''}`);

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        {embedded
          ? <Typography sx={{ fontSize: fs.md, fontWeight: fw.semibold }}>Credit Cards</Typography>
          : <Typography variant="h5" fontWeight={600} color="secondary.main">Credit Cards</Typography>}
        {canWrite && (
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openNew} sx={{ textTransform: 'none' }}>Add Card</Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : cards.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <CardIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography sx={{ fontSize: fs.sm, color: 'text.secondary', mb: 1.5 }}>No credit cards yet.</Typography>
          {canWrite && <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openNew} sx={{ textTransform: 'none' }}>Add Card</Button>}
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {cards.map((c) => {
            const days = daysUntilDue(c);
            const outstanding = Number(c.outstanding || 0);
            const dueSoon = outstanding > 0 && days != null && days <= 7;
            const inactive = c.active === false;
            return (
              <Grid item xs={12} sm={6} lg={4} key={c.id}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', opacity: inactive ? 0.7 : 1, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <CardIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
                    <Typography sx={{ fontSize: fs.md, fontWeight: fw.semibold, flex: 1 }}>
                      {cardLabel(c)} <Typography component="span" sx={{ fontSize: fs.sm, color: 'text.secondary' }}>({c.bankIssuer})</Typography>
                    </Typography>
                    {inactive && <Chip label="Inactive" size="small" sx={{ fontSize: fs.xs, height: 20 }} />}
                    {canWrite && <Tooltip title="Edit card"><IconButton size="small" onClick={() => openEdit(c)}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
                  </Box>
                  <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', mb: 1 }}>{[c.cardName, c.cardholderName].filter(Boolean).join(' · ')}</Typography>
                  <Divider sx={{ mb: 1 }} />
                  <TileRow label="Outstanding">
                    <Typography component="span" sx={{ fontSize: fs.md, fontWeight: fw.bold, color: outstanding > 0 ? 'error.main' : 'success.main' }}>
                      {formatCurrency(outstanding)}
                    </Typography>
                  </TileRow>
                  <TileRow label="Due">
                    {days == null ? '—' : (
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: dueSoon ? 'warning.main' : 'inherit' }}>
                        {ordinal(Number(c.paymentDueDay))} ({days === 0 ? 'today' : `${days} day${days === 1 ? '' : 's'}`})
                        {dueSoon && <WarningIcon sx={{ fontSize: 16 }} />}
                      </Box>
                    )}
                  </TileRow>
                  <TileRow label="This month">{formatCurrency(c.thisMonthCharges)}</TileRow>
                  <TileRow label="Last payment">
                    {c.lastPaymentDate ? `${formatCurrency(c.lastPaymentAmount)} on ${formatDate(c.lastPaymentDate)}` : '—'}
                  </TileRow>
                  <Box sx={{ display: 'flex', gap: 1, mt: 'auto', pt: 1.5 }}>
                    {canWrite && (
                      <Button variant="contained" size="small" startIcon={<PaymentIcon />} onClick={() => open(c, true)} sx={{ textTransform: 'none', flex: 1 }}>
                        Make Payment
                      </Button>
                    )}
                    <Button variant="outlined" size="small" startIcon={<HistoryIcon />} onClick={() => open(c)} sx={{ textTransform: 'none', flex: 1 }}>
                      View Transactions
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={editing !== null} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing?.id ? 'Edit Credit Card' : 'Add Credit Card'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Card type *" value={form.cardType} onChange={set('cardType')}>
                {CARD_TYPES.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Last 4 digits *" value={form.lastFour} onChange={set('lastFour')}
                inputProps={{ maxLength: 4, inputMode: 'numeric' }}
                error={form.lastFour !== '' && !/^\d{4}$/.test(form.lastFour)} helperText="Never the full number" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Card name *" value={form.cardName} onChange={set('cardName')} placeholder="e.g. Office Visa" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Cardholder *" value={form.cardholderName} onChange={set('cardholderName')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Issuing bank *" value={form.bankIssuer} onChange={set('bankIssuer')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" label="Payment due day (1–28)" value={form.paymentDueDay} onChange={set('paymentDueDay')} inputProps={{ min: 1, max: 28 }} />
            </Grid>
            {!editing?.id && (
              <Grid item xs={12}>
                <TextField fullWidth size="small" type="number" label="Opening balance (owed today)" value={form.openingBalance} onChange={set('openingBalance')}
                  InputProps={{ endAdornment: <InputAdornment position="end">AED</InputAdornment>, inputProps: { min: 0, step: '0.01' } }}
                  helperText="Optional. Cannot be changed later" />
              </Grid>
            )}
            {editing?.id && (
              <Grid item xs={12}>
                <FormControlLabel label="Active (an inactive card is hidden from payment pickers but can still be paid)"
                  control={<Switch checked={form.active} onChange={set('active')} />} />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" disabled={busy || !valid} onClick={save}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar(null)} message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};

export default CreditCards;
